import React from 'react'
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components'

const useStyles = makeStyles({
  centred: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalS,
    ...shorthands.padding(tokens.spacingVerticalXXXL, tokens.spacingHorizontalL),
    textAlign: 'center',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '260px',
  },
  skeletonCard: {
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  skeletonStack: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  line: {
    height: '10px',
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    backgroundColor: tokens.colorNeutralBackground3,
    animationName: {
      '0%': { opacity: 0.55 },
      '50%': { opacity: 1 },
      '100%': { opacity: 0.55 },
    },
    animationDuration: '1.6s',
    animationIterationCount: 'infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
  },
  lineTitle: { width: '55%', height: '13px' },
  lineText: { width: '90%' },
  lineMeta: { width: '35%', height: '9px' },
})

/** Placeholder cards shown while a list loads, so the pane never looks empty or broken. */
export const ListSkeleton: React.FC<{ rows?: number; label?: string }> = ({
  rows = 3,
  label = 'Loading',
}) => {
  const styles = useStyles()

  return (
    <div className={styles.skeletonStack} aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div className={styles.skeletonCard} key={i}>
          <span className={`${styles.line} ${styles.lineTitle}`} />
          <span className={`${styles.line} ${styles.lineText}`} />
          <span className={`${styles.line} ${styles.lineMeta}`} />
        </div>
      ))}
    </div>
  )
}

export const LoadingState: React.FC<{ label?: string }> = ({ label = 'Working on it' }) => {
  const styles = useStyles()
  return (
    <div className={styles.centred}>
      <Spinner size="small" label={label} labelPosition="below" />
    </div>
  )
}

export interface EmptyStateProps {
  title: string
  hint?: string
  icon?: React.ReactNode
  action?: React.ReactNode
}

/** Empty states say what the user can do next rather than only that nothing is here. */
export const EmptyState: React.FC<EmptyStateProps> = ({ title, hint, icon, action }) => {
  const styles = useStyles()

  return (
    <div className={styles.centred}>
      {icon}
      <Text weight="semibold">{title}</Text>
      {hint && (
        <Text size={200} className={styles.hint}>
          {hint}
        </Text>
      )}
      {action}
    </div>
  )
}

export interface ErrorStateProps {
  title: string
  /** What actually went wrong, in the user's terms. */
  detail?: string
  onRetry?: () => void
}

/** An error the user can act on: what failed, why, and a way to try again. */
export const ErrorState: React.FC<ErrorStateProps> = ({ title, detail, onRetry }) => (
  <MessageBar intent="error" politeness="assertive">
    <MessageBarBody>
      <MessageBarTitle>{title}</MessageBarTitle>
      {detail}
    </MessageBarBody>
    {onRetry && (
      <MessageBarActions>
        <Button size="small" onClick={onRetry}>
          Try again
        </Button>
      </MessageBarActions>
    )}
  </MessageBar>
)
